import { assertResourceAvailable } from '../../utils/resourceLifecycle';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  AppBar,
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Collapse,
  Slide,
  Stack,
  Toolbar,
  Tooltip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TitleIcon from '@mui/icons-material/Title';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { doc, runTransaction } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  acquireEditLease,
  heartbeatEditLease,
  takeOverEditLease,
  releaseEditLease,
  leaseStartedLabel,
  registerSessionMount,
  consumeSessionMount,
  LEASE_HEARTBEAT_MS,
} from '../../services/editLease';
import { isFunctionRunnable } from '../../services/functionVersions';
import { loadLatestCompletedRunsByFunction } from '../../services/functionRuns';
import {
  GRID_COLS,
  GRID_MARGIN,
  GRID_ROW_HEIGHT,
  KIND_DEFAULT_DISPLAY,
  appendLayoutItem,
  compositeEntries,
  displayDefaultSize,
  findCompositeEntry,
  makeInstanceId,
  makeWidgetId,
  normalizeWidget,
  resolveViewModel,
  withDisplayConstraints,
} from '../../utils/widgetModel';
import EntryTypeIcon from '../common/EntryTypeIcon';
import ViewFunctionCard from './ViewFunctionCard';
import WidgetDisplayEditor from './WidgetDisplayEditor';
import HelpButton from '../common/HelpButton';
import { stableStringify } from '../../utils/stableJson';
import useRunResultHydration from '../../hooks/useRunResultHydration';

// The builder is a full-screen workspace like the Function builder: the grid is
// the point of the screen and a 60%-height dialog leaves the cards cramped.
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// The palette's drag payload. Dropping a FUNCTION creates a card showing that
// function's whole tabbed composite; dropping one of its ENTRIES creates a card
// showing exactly that entry, with the display its kind implies.
const DRAG_MIME = 'application/json';

// What the builder would save, reduced to a comparable string. It mirrors
// saveView's shape rather than the raw state: react-grid-layout decorates layout
// items with bookkeeping fields (`moved`, `static`, ...) on every change, and an
// untouched existing view must not read as dirty the moment its grid mounts.
function draftSignature({ viewName, viewDescription, widgets, layouts, instanceMap }) {
  const items = layouts
    .filter((item) => widgets[item.i])
    .map((item) => {
      const constrained = withDisplayConstraints(item, widgets[item.i].display);
      return {
        i: constrained.i,
        x: constrained.x,
        y: constrained.y,
        w: constrained.w,
        h: constrained.h,
        widget: widgets[item.i],
        functionId: widgets[item.i].instanceId ? (instanceMap[widgets[item.i].instanceId] || null) : null,
      };
    })
    .sort((a, b) => (a.i < b.i ? -1 : a.i > b.i ? 1 : 0));
  return stableStringify({
    name: viewName.trim(),
    description: viewDescription.trim(),
    items,
  });
}

function ViewBuilder({ open, onClose, viewId, companyName, functions, onViewSaved, sessionId }) {
  const { currentUser } = useAuth() || {};
  const actorUid = currentUser?.uid || 'unknown';
  const actorName = currentUser?.displayName || currentUser?.email || 'Another user';
  const actorNameRef = useRef(actorName);
  actorNameRef.current = actorName;
  const [layouts, setLayouts] = useState([]);
  const [widgets, setWidgets] = useState({});
  const [instanceMap, setInstanceMap] = useState({});
  const [viewName, setViewName] = useState('');
  const [viewDescription, setViewDescription] = useState('');
  const [availableFunctions, setAvailableFunctions] = useState([]);
  // functionId -> { run, outputs, entries, fullResultNeeded, fullResultLoaded }.
  // The entry catalog is NOT authored by the user: it is the manifest entries
  // the function has actually produced, read from the latest completed run —
  // from its bounded preview, or from its complete stored result when the run
  // has no preview (`fullResultNeeded` until that result is in, then
  // `fullResultLoaded`).
  const [functionCatalog, setFunctionCatalog] = useState({});
  const functionCatalogRef = useRef(functionCatalog);
  functionCatalogRef.current = functionCatalog;
  const [catalogLoading, setCatalogLoading] = useState(false);

  // A completed run whose composite could not fit the preview budget (one large
  // chart is enough) stores no preview at all, so its entries come from the
  // complete result in storage. The target is the function id; a result is
  // accepted only while the catalog still holds the run it was loaded for.
  const acceptFullResult = useCallback(({ targetId, runId, data }) => {
    const current = functionCatalogRef.current[targetId];
    if (!current?.run || current.run.id !== runId) return false;
    const outputs = data?.outputs && typeof data.outputs === 'object' ? data.outputs : {};
    setFunctionCatalog((previous) => {
      const latest = previous[targetId];
      if (!latest?.run || latest.run.id !== runId) return previous;
      return {
        ...previous,
        [targetId]: {
          ...latest,
          outputs,
          entries: compositeEntries(outputs),
          fullResultNeeded: false,
          fullResultLoaded: true,
        },
      };
    });
    return true;
  }, []);
  const {
    states: fullResultStates,
    hydrate: hydrateFullResult,
    retry: retryFullResult,
  } = useRunResultHydration({
    scopeKey: `${companyName || ''}::view-builder::${viewId || 'new'}`,
    onHydrated: acceptFullResult,
  });
  const [expandedFunctionId, setExpandedFunctionId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [createdAt, setCreatedAt] = useState(null);
  const [editorWidgetId, setEditorWidgetId] = useState(null);
  // The draft as it stood when loading finished; closing compares against it.
  const baselineRef = useRef(null);
  const [discardPromptOpen, setDiscardPromptOpen] = useState(false);

  const gridRef = useRef(null);

  // --- Advisory edit lease --------------------------------------------------
  // One builder session at a time holds an existing view's lease (the same
  // `edit_lease` the Function builder uses). Unlike a Function draft, the view
  // builder does not autosave, so a takeover discards the evicted session's
  // unsaved edits — the dialogs say so. A new, unsaved view has no document to
  // lease ('none').
  const viewRef = useCallback((id) => doc(db, `users/${companyName}/dashboard_views`, id), [companyName]);
  // `sessionId` identifies one editing session and is owned by the Dashboard,
  // which mints it when the builder opens and drops it when it closes. This
  // builder therefore claims the lease ONCE and holds it: a remount rejoins the
  // same session (acquireEditLease treats this session's own lease as
  // available) instead of arriving as a stranger its own live lease refuses.
  const leaseHeldRef = useRef(false);
  const releaseRef = useRef(null);
  // The one claim of this editing session, with the view it is for, so the
  // release path can wait for a claim still in flight.
  const claimRef = useRef(null);
  const mountTokenRef = useRef(null);
  // One editing session of one view: what the claim and the mount registry are
  // both keyed by.
  const editSessionKey = `${sessionId}/${viewId}`;
  const [leaseState, setLeaseState] = useState('none');
  const [blockingLease, setBlockingLease] = useState(null);
  const [takeoverBusy, setTakeoverBusy] = useState(false);
  const leaseLoadGenerationRef = useRef(0);

  // Existing drafts come only from the transaction that granted the lease.
  const applyViewSnapshot = useCallback((viewData) => {
    const model = resolveViewModel(viewData);
    const resolvedMap = { ...(viewData.functionInstanceMap || {}) };
    Object.values(model.widgets).forEach((widget) => {
      if (widget.instanceId && !resolvedMap[widget.instanceId]) {
        resolvedMap[widget.instanceId] = widget.instanceId;
      }
    });
    setViewName(viewData.name || '');
    setViewDescription(viewData.description || '');
    setCreatedAt(viewData.createdAt || null);
    setWidgets(model.widgets);
    setLayouts(model.layouts);
    setInstanceMap(resolvedMap);
    setEditorWidgetId(null);
    setError('');
    baselineRef.current = draftSignature({
      viewName: viewData.name || '', viewDescription: viewData.description || '',
      widgets: model.widgets, layouts: model.layouts, instanceMap: resolvedMap,
    });
    setIsLoading(false);
  }, []);

  const releaseIfHeld = useCallback(async (mountToken) => {
    if (!viewId) return;
    // A claim still in flight has not set leaseHeldRef yet, so wait for it
    // rather than read the flag too early and leave the lease on the view until
    // it expires. A failed claim reports itself and leaves nothing to release.
    const claim = claimRef.current;
    if (claim) await claim.request.catch(() => {});
    // Another mount is editing under this session now, so the lease is being
    // used: this one is on its way out and has nothing to hand back.
    if (!consumeSessionMount(editSessionKey, mountToken)) return;
    if (!leaseHeldRef.current) return;
    leaseHeldRef.current = false;
    await releaseEditLease(viewRef(viewId), sessionId);
  }, [viewRef, viewId, sessionId, editSessionKey]);

  useEffect(() => { releaseRef.current = releaseIfHeld; }, [releaseIfHeld]);

  // While this builder is open on a saved view, this mount is the one editing
  // that session of it. Closing and unmounting both end the session here, so the
  // lease frees at once instead of waiting for the 15 minute expiry. A new,
  // unsaved view has no document to lease and registers nothing.
  useEffect(() => {
    if (!open || !viewId) return undefined;
    const mountToken = registerSessionMount(editSessionKey);
    mountTokenRef.current = mountToken;
    return () => {
      mountTokenRef.current = null;
      const release = releaseRef.current;
      if (release) {
        release(mountToken).catch((err) => console.error('Failed to release the view edit lease:', err));
      }
    };
  }, [editSessionKey, open, viewId]);

  useEffect(() => {
    setTakeoverBusy(false);
    if (!open || !companyName || !viewId) {
      setLeaseState('none');
      // The session is over; reopening this same mount is a new one and claims
      // again, rather than sitting on a lease that was already handed back.
      claimRef.current = null;
      return undefined;
    }
    // One claim per editing session per view. This effect also re-runs when
    // development remounts the builder; that is not a new editing session, so
    // it does not claim again — and must not reopen the loading state, because
    // no second claim would come back to clear it. A new session always claims.
    if (claimRef.current?.claimKey === editSessionKey) return undefined;
    setLeaseState('acquiring');
    setIsLoading(true);
    const claim = {
      claimKey: editSessionKey,
      request: acquireEditLease(viewRef(viewId), {
        uid: actorUid,
        holderName: actorNameRef.current,
        sessionId,
      }, 'view'),
    };
    claimRef.current = claim;
    claim.request
      .then((result) => {
        // A grant puts the lease on the view whatever happens next, so record
        // it before anything else: the release paths read this flag to hand the
        // lease back instead of leaving it to expire.
        if (result.granted) leaseHeldRef.current = true;
        if (claimRef.current !== claim) return;
        if (result.granted) {
          applyViewSnapshot(result.data);
          setLeaseState('held');
        } else {
          setBlockingLease(result.lease);
          setLeaseState('blocked');
        }
      })
      .catch((err) => {
        if (claimRef.current !== claim) return;
        console.error('Failed to acquire the view edit lease:', err);
        setBlockingLease(null);
        setLeaseState('unavailable');
      });
    // A takeover in flight when this builder closes is no longer this session's
    // to apply.
    return () => { leaseLoadGenerationRef.current += 1; };
  }, [open, companyName, viewId, viewRef, actorUid, applyViewSnapshot, sessionId, editSessionKey]);

  useEffect(() => {
    if (leaseState !== 'held' || !companyName || !viewId) return undefined;
    const timer = setInterval(async () => {
      try {
        const outcome = await heartbeatEditLease(viewRef(viewId), sessionId);
        if (outcome !== 'held') {
          leaseHeldRef.current = false;
          setLeaseState(outcome);
        }
      } catch (err) {
        console.error('View edit lease heartbeat failed:', err);
      }
    }, LEASE_HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [leaseState, companyName, viewId, viewRef, sessionId]);

  const handleTakeOver = async () => {
    const generation = ++leaseLoadGenerationRef.current;
    setTakeoverBusy(true);
    // A takeover acquires this session's lease just as the claim does, so it
    // becomes the claim the release waits for: a builder closed while this is
    // in flight hands the lease straight back instead of leaving a dead
    // session's lease on the view for the full 15 minutes.
    const takeover = {
      claimKey: editSessionKey,
      request: takeOverEditLease(viewRef(viewId), {
        uid: actorUid,
        holderName: actorName,
        sessionId,
      }, 'view'),
    };
    claimRef.current = takeover;
    try {
      const result = await takeover.request;
      // The lease is on the view from here on, whatever happened meanwhile.
      leaseHeldRef.current = true;
      if (generation !== leaseLoadGenerationRef.current) return;
      applyViewSnapshot(result.data);
      setBlockingLease(null);
      setLeaseState('held');
    } catch (err) {
      console.error('Failed to take over the view edit lease:', err);
      if (generation === leaseLoadGenerationRef.current) setLeaseState('unavailable');
    } finally {
      if (generation === leaseLoadGenerationRef.current) setTakeoverBusy(false);
    }
  };

  // A new View has no stored document or lease yet.
  useEffect(() => {
    if (open && (!viewId || !companyName)) applyViewSnapshot({});
  }, [open, viewId, companyName, applyViewSnapshot]);

  // A function is selectable for a View when it is RUNNABLE — published and not
  // invalidated by a missing dependency — and its published version produces at
  // least one Output. `output_summaries` is written by every publish, describes
  // the published version and may contain any Output type (including an Email
  // Output, which shows its delivery status after an explicit run).
  useEffect(() => {
    if (functions && Array.isArray(functions)) {
      const runnableFuncs = functions.filter((f) => (
        isFunctionRunnable(f) && f.output_summaries.length > 0
      ));
      setAvailableFunctions(runnableFuncs);
    }
  }, [functions]);

  // Build the entry catalog once, when the builder opens. One shared read of the
  // recent-runs window covers every offered function, continuing through older pages
  // until each Function is found or history ends. A run's bounded preview carries
  // every chart, file and folder entry in full, so it normally answers what a
  // function produces without a storage round trip; a run that stored no preview
  // is marked `fullResultNeeded` and its complete result is loaded below. A
  // function with no completed run in its history simply offers no entries —
  // only the whole-function drag.
  useEffect(() => {
    if (!open || !companyName || availableFunctions.length === 0) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setCatalogLoading(true);
    (async () => {
      try {
        const runsByFunction = await loadLatestCompletedRunsByFunction(
          companyName,
          availableFunctions.map((func) => func.id),
          { signal: controller.signal },
        );
        if (cancelled) return;
        const catalog = {};
        Object.entries(runsByFunction).forEach(([functionId, run]) => {
          if (!run) return;
          // Entries are what the CURRENT version produces. The newest completed
          // run of an earlier version describes an output shape the function may
          // no longer have, so it offers nothing draggable until the published
          // version has completed a run of its own.
          const publishedVersion = availableFunctions.find((func) => func.id === functionId)?.published_version;
          if (run.function_version !== publishedVersion) {
            catalog[functionId] = { run: null, outputs: {}, entries: [], earlierVersionOnly: true };
            return;
          }
          // This effect re-runs whenever the Dashboard's live function list
          // changes, so the entries of a complete result already loaded for
          // this same run are kept, beside the run document just read, rather
          // than downloaded again.
          const previous = functionCatalogRef.current[functionId];
          if (previous?.fullResultLoaded && previous.run.id === run.id) {
            catalog[functionId] = { ...previous, run };
            return;
          }
          const outputs = run.result_preview?.outputs && typeof run.result_preview.outputs === 'object'
            ? run.result_preview.outputs
            : {};
          const fullResultNeeded = !run.result_preview && Boolean(run.result_gcs_path);
          catalog[functionId] = { run, outputs, entries: compositeEntries(outputs), fullResultNeeded };
        });
        setFunctionCatalog(catalog);
      } catch (err) {
        if (!cancelled) console.error('Failed to read the entry catalog:', err);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [open, companyName, availableFunctions]);

  // Load the complete result of every catalogued run that stored no preview.
  // The hook ignores a repeat request for a result it is loading, has loaded,
  // or failed to load (Retry forces that last one).
  useEffect(() => {
    Object.entries(functionCatalog).forEach(([functionId, catalog]) => {
      if (catalog.fullResultNeeded) hydrateFullResult(functionId, catalog.run);
    });
  }, [functionCatalog, hydrateFullResult]);

  // Why a function's entries are not known yet although it has a completed
  // run: '' once they are (or when its run stored a preview).
  const entriesUnknownReasonFor = useCallback((functionId) => {
    if (!functionCatalog[functionId]?.fullResultNeeded) return '';
    return fullResultStates[functionId]?.status === 'error'
      ? "This run's entries could not be loaded."
      : "Loading this run's entries…";
  }, [functionCatalog, fullResultStates]);

  // Update dynamic width calculation for GridLayout
  useEffect(() => {
    const handleResize = () => {
      if (gridRef.current) {
        // Forcing a re-render with new dimensions
        setLayouts((prev) => [...prev]);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Widgets of the same function share ONE instance, so they share one parameter
  // group in the sidebar and one run updates all of them together.
  const instanceForFunction = useCallback((functionId) => {
    const existing = Object.entries(instanceMap).find(([, mappedId]) => mappedId === functionId);
    if (existing) return existing[0];
    return makeInstanceId(functionId);
  }, [instanceMap]);

  const addWidget = useCallback((widget) => {
    const widgetId = makeWidgetId();
    const size = displayDefaultSize(widget.display);
    setWidgets((prev) => ({ ...prev, [widgetId]: widget }));
    setLayouts((prev) => [
      ...prev,
      appendLayoutItem(prev, { i: widgetId, x: 0, ...size }, widget.display),
    ]);
    return widgetId;
  }, []);

  const handleDrop = (event) => {
    event.preventDefault();
    try {
      const raw = event.dataTransfer.getData(DRAG_MIME);
      if (!raw) return;
      const payload = JSON.parse(raw);
      if (!payload?.functionId && payload?.type !== 'header') return;

      if (payload.type === 'function') {
        const instanceId = instanceForFunction(payload.functionId);
        setInstanceMap((prev) => ({ ...prev, [instanceId]: payload.functionId }));
        addWidget(normalizeWidget({ instanceId, binding: null, display: 'tabs', config: {}, title: null }));
        return;
      }

      if (payload.type === 'entry') {
        const instanceId = instanceForFunction(payload.functionId);
        setInstanceMap((prev) => ({ ...prev, [instanceId]: payload.functionId }));
        const display = KIND_DEFAULT_DISPLAY[payload.kind] || 'tabs';
        addWidget(normalizeWidget({
          instanceId,
          binding: { source_block_id: payload.source_block_id, entry_key: payload.entry_key },
          display,
          config: display === 'stat' ? { label: payload.label } : {},
          title: null,
        }));
      }
    } catch (err) {
      console.error("Error handling drop:", err);
      setError("Failed to add this card to the view");
    }
  };

  const handleAddHeader = () => {
    addWidget(normalizeWidget({
      instanceId: null,
      binding: null,
      display: 'header',
      config: { text: 'Section title', subtitle: '' },
      title: null,
    }));
  };

  const handleLayoutChange = (newLayout) => {
    setLayouts(newLayout.map((item) => (
      widgets[item.i] ? withDisplayConstraints(item, widgets[item.i].display) : item
    )));
  };

  const handleRemoveWidget = (widgetId) => {
    setWidgets((prev) => {
      const next = { ...prev };
      delete next[widgetId];
      return next;
    });
    setLayouts((prev) => prev.filter(item => item.i !== widgetId));
  };

  const handleWidgetSave = (widgetId, nextWidget) => {
    setWidgets((prev) => ({ ...prev, [widgetId]: nextWidget }));
    setLayouts((prev) => prev.map((item) => (
      item.i === widgetId ? withDisplayConstraints(item, nextWidget.display) : item
    )));
    setEditorWidgetId(null);
  };

  // Cards whose function no longer exists cannot be saved: the view would render
  // a permanent error tile.
  // A view is a place to run functions: headers alone have nothing to run, so
  // a view without a function card cannot be saved.
  const hasFunctionCard = useMemo(
    () => Object.values(widgets).some((widget) => widget?.instanceId),
    [widgets],
  );

  const unresolvedWidgetIds = useMemo(() => (
    Object.entries(widgets)
      .filter(([, widget]) => {
        if (!widget.instanceId) return false;
        const functionId = instanceMap[widget.instanceId];
        return !functions?.some((func) => func.id === functionId);
      })
      .map(([widgetId]) => widgetId)
  ), [widgets, instanceMap, functions]);

  // The editor needs the same instance shape ViewDisplay builds, but sourced from
  // the catalog: the builder never runs anything.
  const editorInstanceState = useMemo(() => {
    const widget = editorWidgetId ? widgets[editorWidgetId] : null;
    if (!widget?.instanceId) return null;
    const functionId = instanceMap[widget.instanceId];
    const func = functions?.find((candidate) => candidate.id === functionId);
    if (!func) return null;
    const catalog = functionCatalog[functionId];
    // Until a run with no preview has had its complete result loaded, its
    // entries are unknown: the editor offers no run to choose entries from and
    // says why, rather than claiming the run produced none.
    const entriesUnknownReason = entriesUnknownReasonFor(functionId);
    const entriesFailed = fullResultStates[functionId]?.status === 'error';
    const run = entriesUnknownReason ? null : (catalog?.run || null);
    return {
      functionId,
      functionName: func.name || func.id,
      run,
      outputs: catalog?.outputs || {},
      outputsSummary: run?.outputs_summary || null,
      fullLoaded: false,
      loading: Boolean(entriesUnknownReason) && !entriesFailed,
      error: entriesFailed ? entriesUnknownReason : '',
      entriesUnknownReason,
      // The editor is a modal over the palette, so the palette's Retry cannot
      // be reached from it: a failed download is retried from the editor too.
      onRetryEntries: entriesFailed ? () => retryFullResult(functionId, catalog.run) : null,
      truncatedTables: [],
    };
  }, [editorWidgetId, widgets, instanceMap, functions, functionCatalog, fullResultStates, entriesUnknownReasonFor, retryFullResult]);

  const handleSave = async () => {
    if (!viewName.trim()) {
      setNameDialogOpen(true);
      return;
    }

    try {
      await saveView();
    } catch (err) {
      console.error("Error saving view:", err);
      setError("Failed to save view");
    }
  };

  const saveView = async () => {
    if (!companyName) {
      setError("Company name is not available");
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const viewDocId = viewId || `view_${Date.now()}_${Math.floor(Math.random()*1000)}`;

      // Only widgets that still have a layout entry are saved, and only the
      // instances those widgets reference are kept in the map.
      const cleanWidgets = {};
      const cleanLayouts = [];
      layouts.forEach((item) => {
        const widget = widgets[item.i];
        if (!widget) return;
        cleanWidgets[item.i] = widget;
        const constrained = withDisplayConstraints(item, widget.display);
        cleanLayouts.push({
          i: constrained.i,
          x: constrained.x,
          y: constrained.y,
          w: constrained.w,
          h: constrained.h,
          minW: constrained.minW,
          minH: constrained.minH,
        });
      });

      const functionInstanceMap = {};
      const originalFunctionIds = [];
      Object.values(cleanWidgets).forEach((widget) => {
        if (!widget.instanceId) return;
        const functionId = instanceMap[widget.instanceId] || widget.instanceId;
        functionInstanceMap[widget.instanceId] = functionId;
        if (!originalFunctionIds.includes(functionId)) originalFunctionIds.push(functionId);
      });

      // This builder owns the shared layout and display definition. Filter
      // values are never saved: the live View keeps them locally.
      const builderFields = {
        name: viewName.trim() || `View ${new Date().toLocaleDateString()}`,
        description: viewDescription.trim() || '',
        layouts: cleanLayouts,
        widgets: cleanWidgets,
        functionIds: originalFunctionIds,
        functionInstanceMap,
        updatedAt: new Date().toISOString(),
      };

      let viewData;
      if (viewId) {
        const ref = viewRef(viewId);
        viewData = await runTransaction(db, async (tx) => {
          const snapshot = await tx.get(ref);
          if (!snapshot.exists()) throw new Error('This view no longer exists.');
          assertResourceAvailable(snapshot, 'View');
          const current = snapshot.data() || {};
          const lease = current.edit_lease || null;
          if (!lease || lease.session_id !== sessionId) {
            throw new Error('Your editing session ended — another session took over this view. Reopen the builder to edit it.');
          }
          for (const functionId of originalFunctionIds) {
            const functionRef = doc(db, `users/${companyName}/data_functions`, functionId);
            assertResourceAvailable(await tx.get(functionRef), 'Function');
          }
          // An update, not a replacement: the lease and any field this editor
          // does not own stay exactly as they are.
          tx.update(ref, builderFields);
          return { ...builderFields, createdAt: current.createdAt || createdAt || null };
        });
      } else {
        viewData = {
          ...builderFields,
          createdAt: new Date().toISOString(),
        };
        await runTransaction(db, async (tx) => {
          for (const functionId of originalFunctionIds) {
            const functionRef = doc(db, `users/${companyName}/data_functions`, functionId);
            assertResourceAvailable(await tx.get(functionRef), 'Function');
          }
          tx.set(viewRef(viewDocId), viewData);
        });
      }

      if (onViewSaved) {
        onViewSaved(viewDocId, viewData);
      }

      onClose();
    } catch (err) {
      console.error("Error saving view:", err);
      setError("Failed to save view: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNameDialogSave = () => {
    if (!viewName.trim()) {
      return;
    }
    setNameDialogOpen(false);
    saveView();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Every way out of the builder other than a successful save: backdrop, Escape,
  // the close icon and Cancel. A changed draft is confirmed before it is lost.
  const handleRequestClose = () => {
    if (isSaving) return;
    const current = draftSignature({ viewName, viewDescription, widgets, layouts, instanceMap });
    if (baselineRef.current !== null && current === baselineRef.current) {
      onClose();
      return;
    }
    setDiscardPromptOpen(true);
  };

  if (leaseState === 'blocked' || leaseState === 'unavailable') {
    return (
      <Dialog open onClose={() => onClose()} maxWidth="sm" fullWidth>
        <DialogTitle>This view is being edited</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {leaseState === 'blocked'
              ? `${blockingLease?.holder_name || 'Another user'} is editing this view (started ${leaseStartedLabel(blockingLease)}).`
              : 'The edit lock for this view could not be read.'}
            {' '}Taking over ends their editing session. Any changes they have not saved will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()} disabled={takeoverBusy}>Cancel</Button>
          <Button onClick={handleTakeOver} variant="contained" disabled={takeoverBusy}>
            {takeoverBusy ? 'Taking over…' : 'Take over'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (leaseState === 'lost' || leaseState === 'deleted') {
    return (
      <Dialog open onClose={() => onClose()} maxWidth="sm" fullWidth>
        <DialogTitle>{leaseState === 'deleted' ? 'This view was deleted' : 'Editing session ended'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {leaseState === 'deleted'
              ? 'Another user deleted this view while you were editing it, so this editor has been closed. '
                + 'Changes you had not saved were not kept.'
              : 'Another session took over editing this view, so this editor has been closed. Changes you '
                + 'had not saved were not kept.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (isLoading || leaseState === 'acquiring') {
    return (
      <Dialog fullScreen open={open} TransitionComponent={Transition}>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={onClose}>
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">Loading View Builder…</Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog fullScreen open={open} onClose={handleRequestClose} TransitionComponent={Transition}>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleRequestClose}>
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
              {viewId ? 'Edit View' : 'Create New View'}
            </Typography>
            <HelpButton
              docPath="dashboard-page/views/building-a-view"
              tooltip="View Builder Documentation"
            />
            <Button color="inherit" onClick={handleRequestClose} sx={{ ml: 1 }}>Cancel</Button>
            <Button
              color="inherit"
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={isSaving || !hasFunctionCard || unresolvedWidgetIds.length > 0}
              sx={{ ml: 1 }}
            >
              {isSaving ? 'Saving...' : 'Save View'}
            </Button>
          </Toolbar>
        </AppBar>

        {/* The workspace owns the full remaining height so the grid — not the
            dialog — is what grows. */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 2, gap: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          {unresolvedWidgetIds.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {unresolvedWidgetIds.length === 1
                ? '1 card references a deleted function — remove it before saving.'
                : `${unresolvedWidgetIds.length} cards reference deleted functions — remove them before saving.`}
            </Alert>
          )}
          {!hasFunctionCard && (
            <Alert severity="info" sx={{ mb: 2 }}>
              A View needs at least one Function card — headers alone have nothing to run.
            </Alert>
          )}

          {/* Name and description share one compact row so the grid keeps the
              rest of the screen. */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flexShrink: 0 }}>
            <TextField
              label="View Name"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              size="small"
              sx={{ width: { xs: '100%', md: 320 } }}
            />
            <TextField
              label="Description"
              value={viewDescription}
              onChange={(e) => setViewDescription(e.target.value)}
              size="small"
              fullWidth
            />
          </Stack>

          <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, mt: 0 }}>
            {/* Left panel — the palette of functions and the entries they produce */}
            <Grid item xs={3} sx={{ height: '100%' }}>
              <Paper sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6">Available Functions</Typography>
                  {catalogLoading && <CircularProgress size={16} />}
                </Box>
                <Button
                  size="small"
                  startIcon={<TitleIcon />}
                  onClick={handleAddHeader}
                  sx={{ mb: 1 }}
                >
                  Add header
                </Button>
                <Divider sx={{ mb: 2 }} />

                {availableFunctions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No functions available. Create and publish a function first.
                  </Typography>
                ) : (
                  availableFunctions.map(func => {
                    const catalog = functionCatalog[func.id];
                    const entries = catalog?.entries || [];
                    const entriesUnknownReason = entriesUnknownReasonFor(func.id);
                    const entriesFailed = fullResultStates[func.id]?.status === 'error';
                    const expanded = expandedFunctionId === func.id;
                    return (
                      <Box key={func.id} sx={{ mb: 1 }}>
                        <Box
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData(DRAG_MIME, JSON.stringify({
                              type: 'function',
                              functionId: func.id,
                            }));
                          }}
                          sx={{
                            p: 1,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            cursor: 'grab',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <ViewCompactIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                          <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                            {func.name || func.id}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedFunctionId(expanded ? null : func.id)}
                          >
                            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </Box>

                        <Collapse in={expanded} unmountOnExit>
                          <Stack spacing={0.5} sx={{ pl: 1.5, pt: 0.75 }}>
                            {entriesUnknownReason ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                {!entriesFailed && <CircularProgress size={12} />}
                                <Typography variant="caption" color={entriesFailed ? 'error' : 'text.secondary'}>
                                  {entriesFailed
                                    ? `${entriesUnknownReason} Retry, or drag the function itself.`
                                    : entriesUnknownReason}
                                </Typography>
                                {entriesFailed && (
                                  <Button
                                    size="small"
                                    onClick={() => retryFullResult(func.id, catalog.run)}
                                    sx={{ textTransform: 'none', minWidth: 0, py: 0 }}
                                  >
                                    Retry
                                  </Button>
                                )}
                              </Box>
                            ) : entries.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                {catalog?.earlierVersionOnly
                                  ? 'The current version has not completed a run yet, so its entries are not known — run it first, or drag the function itself.'
                                  : catalog
                                    ? 'This run produced no entries — drag the function itself.'
                                    : 'No completed run yet — drag the function itself.'}
                              </Typography>
                            ) : entries.map((entry) => {
                              return (
                                <Tooltip key={`${entry.binding.source_block_id}::${entry.binding.entry_key}`} title={entry.meta}>
                                  <Box
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData(DRAG_MIME, JSON.stringify({
                                        type: 'entry',
                                        functionId: func.id,
                                        source_block_id: entry.binding.source_block_id,
                                        entry_key: entry.binding.entry_key,
                                        kind: entry.kind,
                                        label: entry.label,
                                      }));
                                    }}
                                    sx={{
                                      p: 0.75,
                                      border: (theme) => `1px solid ${theme.palette.divider}`,
                                      borderRadius: 1,
                                      cursor: 'grab',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.75,
                                      '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                  >
                                    <EntryTypeIcon
                                      kind={entry.kind}
                                      filename={entry.filename}
                                      mime={entry.mime}
                                      sx={{ fontSize: 15, color: 'text.disabled' }}
                                    />
                                    <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
                                      {entry.label}
                                    </Typography>
                                    <Chip size="small" variant="outlined" label={entry.kind} sx={{ height: 18, fontSize: '0.62rem' }} />
                                  </Box>
                                </Tooltip>
                              );
                            })}
                          </Stack>
                        </Collapse>
                      </Box>
                    );
                  })
                )}
              </Paper>
            </Grid>

            {/* Right panel - Dashboard grid */}
            <Grid item xs={9} sx={{ height: '100%' }}>
              <Paper
                sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <Typography variant="h6" gutterBottom>Dashboard Layout</Typography>
                <Divider sx={{ mb: 2 }} />

                <Box
                  ref={gridRef}
                  sx={{
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    bgcolor: 'background.default',
                    borderRadius: 1
                  }}
                >
                  {layouts.length === 0 ? (
                    <Box sx={{
                      height: '100%',
                      minHeight: 240,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                      textAlign: 'center'
                    }}>
                      <Typography variant="body1" color="text.secondary">
                        Drag a function — or one of its output entries — from the left panel to add a card.
                      </Typography>
                    </Box>
                  ) : (
                    <GridLayout
                      className="layout"
                      layout={layouts}
                      cols={GRID_COLS}
                      rowHeight={GRID_ROW_HEIGHT}
                      width={gridRef.current ? gridRef.current.offsetWidth - 40 : 800}
                      onLayoutChange={handleLayoutChange}
                      draggableHandle=".drag-handle"
                      // react-grid-layout swallows mousedown on anything inside
                      // a draggable region, which would stop a card's ⚙ and ✕
                      // from ever firing. The handle is already scoped to the
                      // grip, and this is the belt-and-braces half of that.
                      draggableCancel=".MuiIconButton-root,button"
                      compactType="vertical"
                      preventCollision={false}
                      resizeHandles={['se']}
                      margin={[GRID_MARGIN, GRID_MARGIN]}
                    >
                      {layouts.map((layout) => {
                        const widget = widgets[layout.i];
                        if (!widget) return <div key={layout.i} />;
                        const functionId = widget.instanceId ? instanceMap[widget.instanceId] : null;
                        const func = functionId
                          ? functions?.find((candidate) => candidate.id === functionId)
                          : null;
                        const catalog = functionId ? functionCatalog[functionId] : null;
                        const entry = widget.binding && catalog
                          ? findCompositeEntry(catalog.outputs, widget.binding)
                          : null;
                        return (
                          <div key={layout.i}>
                            <ViewFunctionCard
                              widgetId={layout.i}
                              widget={widget}
                              functionName={func ? (func.name || func.id) : ''}
                              entryLabel={entry ? (entry.chart?.title || entry.filename || entry.key) : null}
                              missing={Boolean(widget.instanceId) && !func}
                              onRemove={handleRemoveWidget}
                              onConfigure={setEditorWidgetId}
                            />
                          </div>
                        );
                      })}
                    </GridLayout>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Dialog>

      <WidgetDisplayEditor
        open={Boolean(editorWidgetId && widgets[editorWidgetId])}
        widgetId={editorWidgetId}
        widget={editorWidgetId ? widgets[editorWidgetId] : null}
        instanceState={editorInstanceState}
        companyName={companyName}
        onClose={() => setEditorWidgetId(null)}
        onSave={handleWidgetSave}
      />

      {/* Name dialog */}
      <Dialog open={nameDialogOpen} onClose={() => setNameDialogOpen(false)}>
        <DialogTitle>Name Your View</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="View Name"
            fullWidth
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleNameDialogSave}
            disabled={!viewName.trim()}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discard prompt */}
      <Dialog open={discardPromptOpen} onClose={() => setDiscardPromptOpen(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This view has changes that have not been saved. Closing the builder will discard them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardPromptOpen(false)}>Keep editing</Button>
          <Button
            color="error"
            onClick={() => {
              setDiscardPromptOpen(false);
              onClose();
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ViewBuilder;
